// Faza 10 — Sport hub stranice (klasteri po sportu).
// Hvataju upite tipa "fizioterapeut za trkače", "rehabilitacija fudbalera".

export interface SportI18n {
  metaTitle: string;
  metaDesc: string;
  h1: string;
  intro: string;
  /** Najčešće povrede ovog sporta (krajevi se renderuju kao kartice) */
  injuries: { name: string; description: string; diagnosisSlug?: string }[];
  /** Klinički protokol — koraci */
  protocol: { title: string; description: string }[];
  /** Vodič za prevenciju (bullets) */
  prevention: string[];
  faqs: { question: string; answer: string }[];
}

export interface Sport {
  slug: string; // npr. "fizioterapija-za-trkace"
  name: { sr: string; en: string; ru: string };
  /** Interni linkovi ka uslugama */
  relatedServices: { path: string; label: { sr: string; en: string; ru: string } }[];
  /** Povezane dijagnoze (interno linkovanje) */
  relatedDiagnoses: string[];
  /** Povezane knowledge entry stranice */
  relatedKnowledge?: string[];
  i18n: { sr: SportI18n; en: SportI18n; ru: SportI18n };
}

export const SPORTS: Sport[] = [
  {
    slug: 'fizioterapija-za-trkace',
    name: { sr: 'Fizioterapija za trkače', en: 'Physical therapy for runners', ru: 'Физиотерапия для бегунов' },
    relatedServices: [
      { path: '/fizikalna-terapija-novi-beograd', label: { sr: 'Fizikalna terapija', en: 'Physical therapy', ru: 'Физиотерапия' } },
      { path: '/shockwave-terapija-novi-beograd', label: { sr: 'Shockwave terapija', en: 'Shockwave therapy', ru: 'УВТ' } },
      { path: '/services/functional-training', label: { sr: 'Funkcionalni trening', en: 'Functional training', ru: 'Функциональный тренинг' } },
    ],
    relatedDiagnoses: [
      'ahilova-tetiva-tretman-beograd',
      'petni-trn',
      'bol-u-kolenu-tretman-beograd',
      'meniskus-rehabilitacija-beograd',
    ],
    relatedKnowledge: ['anatomija-kolena'],
    i18n: {
      sr: {
        metaTitle: 'Fizioterapija za trkače Beograd | PHYSIOWRLD',
        metaDesc: 'Specijalizovana fizioterapija za trkače u Beogradu — Achilles, plantarni fascitis, runner\'s knee, IT band sindrom. PHYSIOWRLD klinika.',
        h1: 'Fizioterapija za trkače — rehabilitacija i prevencija',
        intro: 'Trčanje je sport visokog opterećenja koji generiše karakteristične mikropovrede mekih tkiva. U PHYSIOWRLD klinici primenjujemo specifične protokole za najčešće povrede trkača — od Achilles tendinopatije do plantarnog fascitisa.',
        injuries: [
          { name: 'Ahilova tendinopatija', description: 'Bol na donjem delu lista, gori ujutru i posle treninga. Shockwave + Alfredson protokol.', diagnosisSlug: 'ahilova-tetiva-tretman-beograd' },
          { name: 'Plantarni fascitis / petni trn', description: 'Bol u peti pri prvim koracima ujutru. Shockwave je terapija prvog izbora.', diagnosisSlug: 'petni-trn' },
          { name: 'Runner\'s knee (PFPS)', description: 'Patelofemoralni sindrom — bol oko ili iza čašice. Korekcija mehanike kuka i jačanje kvadricepsa.', diagnosisSlug: 'bol-u-kolenu-tretman-beograd' },
          { name: 'IT band sindrom', description: 'Bol sa spoljne strane kolena posle 15–20 minuta trčanja. Manuelne tehnike + jačanje gluteusa.' },
          { name: 'Stresne frakture potkolenice', description: 'Shin splints i stresne reakcije tibije. Privremeno smanjenje opterećenja + progresivni return to run protokol.' },
          { name: 'Povreda meniskusa', description: 'Najčešće degenerativna kod dugogodišnjih trkača. Konzervativni protokol prvi izbor.', diagnosisSlug: 'meniskus-rehabilitacija-beograd' },
        ],
        protocol: [
          { title: '1. Klinička procena i analiza trka', description: 'Funkcionalna procena, testovi mobilnosti i video analiza pokreta tokom trčanja.' },
          { title: '2. Akutna faza — kontrola simptoma', description: 'HILT laser, kineziotaping, ručne tehnike za smanjenje bola.' },
          { title: '3. Tkivna remodulacija', description: 'Shockwave protokol kod tendinopatija (3–5 seansi).' },
          { title: '4. Jačanje i korekcija mehanike', description: 'Funkcionalni trening sa fokusom na gluteus medius, kor i ekscentrike za listove.' },
          { title: '5. Return to run', description: 'Progresivni walk-run protokol uz monitoring tegoba.' },
        ],
        prevention: [
          'Postupno povećavajte nedeljnu kilometražu (pravilo 10 %).',
          'Uključite 2 jaka treninga snage nedeljno — gluteus, kor, listovi.',
          'Rotirajte 2 para patika sa različitom geometrijom.',
          'Ne preskačite warm-up — 5–10 min dinamičkog mobiliteta.',
          'Slušajte rana upozorenja — bol koji ne prolazi posle 3 treninga zahteva procenu.',
        ],
        faqs: [
          { question: 'Mogu li nastaviti da trčim sa bolom u Achillesu?', answer: 'Zavisi od stepena. Bol VAS do 3/10 koji ne raste tokom trčanja i prolazi do sledećeg dana može se nastaviti uz adaptaciju opterećenja. Sve preko zahteva pauzu i procenu.' },
          { question: 'Da li je Shockwave bezbedan tokom sezone?', answer: 'Da. Protokoli se prilagođavaju kalendaru trčanja — najčešće se trening prilagodi u nedelji posle seanse.' },
          { question: 'Koliko traje rehabilitacija plantarnog fascitisa?', answer: 'Kod hroničnih slučajeva tipično 6–10 nedelja kombinovanog Shockwave + vežbe protokola.' },
        ],
      },
      en: {
        metaTitle: 'Physical therapy for runners Belgrade | PHYSIOWRLD',
        metaDesc: 'Specialized PT for runners in Belgrade — Achilles, plantar fasciitis, runner\'s knee, IT band. PHYSIOWRLD clinic.',
        h1: 'Physical therapy for runners — rehab and prevention',
        intro: 'Running is a high-load sport with characteristic soft-tissue injuries. PHYSIOWRLD applies specific protocols for the most common runner issues.',
        injuries: [
          { name: 'Achilles tendinopathy', description: 'Lower calf pain, worse in the morning and after training. Shockwave + Alfredson protocol.', diagnosisSlug: 'ahilova-tetiva-tretman-beograd' },
          { name: 'Plantar fasciitis / heel spur', description: 'Heel pain on first morning steps. Shockwave is first-line.', diagnosisSlug: 'petni-trn' },
          { name: 'Runner\'s knee (PFPS)', description: 'Pain around the kneecap. Hip mechanics correction + quad strengthening.', diagnosisSlug: 'bol-u-kolenu-tretman-beograd' },
          { name: 'IT band syndrome', description: 'Outer knee pain after 15–20 min of running. Manual + glute strengthening.' },
          { name: 'Tibial stress reactions', description: 'Shin splints and tibial stress. Load reduction + progressive return-to-run.' },
          { name: 'Meniscus injury', description: 'Often degenerative in veteran runners. Conservative protocol first.', diagnosisSlug: 'meniskus-rehabilitacija-beograd' },
        ],
        protocol: [
          { title: '1. Clinical assessment and gait analysis', description: 'Functional assessment, mobility tests and video gait analysis.' },
          { title: '2. Acute phase — symptom control', description: 'HILT laser, kinesiotaping, manual techniques.' },
          { title: '3. Tissue remodulation', description: 'Shockwave protocol for tendinopathies (3–5 sessions).' },
          { title: '4. Strengthening and mechanics', description: 'Functional training focused on glute med, core, eccentrics for calves.' },
          { title: '5. Return to run', description: 'Progressive walk-run protocol with symptom monitoring.' },
        ],
        prevention: [
          'Increase weekly mileage gradually (10% rule).',
          'Two strength sessions per week — glutes, core, calves.',
          'Rotate two shoe models with different geometry.',
          'Don\'t skip warm-up — 5–10 min dynamic mobility.',
          'Pain that persists across three sessions needs assessment.',
        ],
        faqs: [
          { question: 'Can I keep running with Achilles pain?', answer: 'Depends on grade. VAS up to 3/10, non-increasing and resolved by next day, may continue with load adjustment. Anything more needs a break and assessment.' },
          { question: 'Is Shockwave safe in-season?', answer: 'Yes. Protocols are adjusted to the running calendar — training is typically modified the week after a session.' },
          { question: 'How long for plantar fasciitis rehab?', answer: 'Chronic cases typically 6–10 weeks of combined Shockwave + exercise.' },
        ],
      },
      ru: {
        metaTitle: 'Физиотерапия для бегунов Белград | PHYSIOWRLD',
        metaDesc: 'Специализированная физиотерапия для бегунов в Белграде — ахилл, плантарный фасциит, колено бегуна. PHYSIOWRLD.',
        h1: 'Физиотерапия для бегунов — реабилитация и профилактика',
        intro: 'Бег — спорт высокой нагрузки с характерными микротравмами. PHYSIOWRLD применяет специальные протоколы для частых проблем бегунов.',
        injuries: [
          { name: 'Ахиллова тендинопатия', description: 'Боль в нижней части икры. УВТ + Альфредсон.', diagnosisSlug: 'ahilova-tetiva-tretman-beograd' },
          { name: 'Плантарный фасциит', description: 'Боль в пятке утром. УВТ — первая линия.', diagnosisSlug: 'petni-trn' },
          { name: 'Колено бегуна (PFPS)', description: 'Коррекция механики бедра и укрепление квадрицепса.', diagnosisSlug: 'bol-u-kolenu-tretman-beograd' },
          { name: 'Синдром ИТ-тракта', description: 'Боль с внешней стороны колена.' },
          { name: 'Стрессовые реакции голени', description: 'Снижение нагрузки + протокол возврата к бегу.' },
        ],
        protocol: [
          { title: '1. Оценка и анализ бега', description: 'Функциональные тесты, видеоанализ.' },
          { title: '2. Острая фаза', description: 'HILT-лазер, тейпы, мануальные техники.' },
          { title: '3. Ремоделирование тканей', description: 'УВТ при тендинопатиях (3–5 сеансов).' },
          { title: '4. Укрепление', description: 'Глют. медиус, кор, эксцентрика икр.' },
          { title: '5. Возврат к бегу', description: 'Прогрессивный walk-run.' },
        ],
        prevention: ['Правило 10% по километражу', 'Две силовые в неделю', 'Две пары кроссовок', 'Разминка 5–10 мин', 'Боль >3 тренировок — оценка'],
        faqs: [
          { question: 'Можно ли бегать с болью в ахилле?', answer: 'До 3/10 VAS без нарастания — можно с адаптацией. Больше — пауза и оценка.' },
          { question: 'УВТ в сезоне?', answer: 'Да, с адаптацией тренировок.' },
          { question: 'Сколько лечить плантарный фасциит?', answer: 'Хронические случаи 6–10 недель УВТ + упражнения.' },
        ],
      },
    },
  },
  {
    slug: 'fizioterapija-za-fudbalere',
    name: { sr: 'Fizioterapija za fudbalere', en: 'Physical therapy for footballers', ru: 'Физиотерапия для футболистов' },
    relatedServices: [
      { path: '/fizikalna-terapija-novi-beograd', label: { sr: 'Fizikalna terapija', en: 'Physical therapy', ru: 'Физиотерапия' } },
      { path: '/shockwave-terapija-novi-beograd', label: { sr: 'Shockwave terapija', en: 'Shockwave therapy', ru: 'УВТ' } },
      { path: '/services/functional-training', label: { sr: 'Funkcionalni trening', en: 'Functional training', ru: 'Функциональный тренинг' } },
    ],
    relatedDiagnoses: [
      'acl-rehabilitacija-beograd',
      'meniskus-rehabilitacija-beograd',
      'bol-u-kolenu-tretman-beograd',
      'ahilova-tetiva-tretman-beograd',
    ],
    i18n: {
      sr: {
        metaTitle: 'Fizioterapija za fudbalere Beograd | PHYSIOWRLD',
        metaDesc: 'Rehabilitacija fudbalskih povreda u Beogradu — ACL, meniskus, hamstring, prepone. Sport-specific protokoli PHYSIOWRLD klinike.',
        h1: 'Fizioterapija za fudbalere — sport-specific rehabilitacija',
        intro: 'Fudbal kombinuje sprintove, kontakt, rotacije i nagle zaustave — sport sa najvišom incidencijom povreda ACL-a, meniskusa i hamstringa. PHYSIOWRLD primenjuje protokole prilagođene položaju igrača i fazi sezone.',
        injuries: [
          { name: 'Ruptura ACL-a', description: 'Najteža fudbalska povreda. 6–9 meseci faza-po-faza rehabilitacije sa Return to Sport testovima.', diagnosisSlug: 'acl-rehabilitacija-beograd' },
          { name: 'Povreda meniskusa', description: 'Često uz ACL. Konzervativni ili postoperativni protokol.', diagnosisSlug: 'meniskus-rehabilitacija-beograd' },
          { name: 'Hamstring strain', description: 'Najčešća meka povreda kod sprintera. Nordijske vežbe + progresivni sprint protokol.' },
          { name: 'Adduktorni sindrom (prepone)', description: 'Hroničan bol u preponama — Copenhagen Adduction protokol.' },
          { name: 'Distorzija skočnog zgloba', description: 'Akutni protokol + neuromuskularni trening za prevenciju recidiva.' },
          { name: 'Achilles tendinopatija', description: 'Visoki rizik kod golmana i igrača sa intenzivnim skokovima.', diagnosisSlug: 'ahilova-tetiva-tretman-beograd' },
        ],
        protocol: [
          { title: '1. Procena i klinički testovi', description: 'Lachman, McMurray, Thessaly — uz funkcionalne testove (Y-Balance, hop testovi).' },
          { title: '2. Akutna faza i kontrola otoka', description: 'HILT laser, manuelni protokol, kineziotaping.' },
          { title: '3. Vraćanje pokretljivosti', description: 'Manuelne tehnike + pasivna i aktivna mobilnost.' },
          { title: '4. Snaga i neuromuskularna kontrola', description: 'Progresivni jaki rad, plyometrika, single-leg stabilnost.' },
          { title: '5. Sport-specific drilovi', description: 'Sprintovi, cutting, kick mehanika — fazno povećanje opterećenja.' },
          { title: '6. Return to Play testovi', description: 'Hop testovi, izokinetička procena, klinička procena pre povratka u tim.' },
        ],
        prevention: [
          'FIFA 11+ warm-up pre svakog treninga.',
          'Nordijske vežbe za hamstring 2x nedeljno.',
          'Copenhagen Adduction za prepone.',
          'Single-leg balance trening za prevenciju distorzija.',
          'Adekvatan oporavak — minimum 48h između teških treninga.',
        ],
        faqs: [
          { question: 'Koliko traje povratak na teren posle ACL operacije?', answer: 'Standard je 9–12 meseci do bezbednog povratka na utakmice, uz uspešno položene Return to Sport testove.' },
          { question: 'Mogu li igrati sa hroničnim bolom u preponama?', answer: 'Kratkoročno — uz adaptaciju opterećenja i terapiju. Dugoročno bez tretmana može preći u sportsku hernija.' },
          { question: 'Da li su preventivni programi efikasni?', answer: 'Da. FIFA 11+ smanjuje incidenciju ozbiljnih povreda za 30–50% prema meta-analizama.' },
        ],
      },
      en: {
        metaTitle: 'Physical therapy for footballers Belgrade | PHYSIOWRLD',
        metaDesc: 'Football injury rehab in Belgrade — ACL, meniscus, hamstring, groin. Sport-specific protocols at PHYSIOWRLD clinic.',
        h1: 'Physical therapy for footballers — sport-specific rehab',
        intro: 'Football combines sprints, contact, rotations and rapid decelerations — the sport with the highest ACL, meniscus and hamstring incidence. PHYSIOWRLD applies protocols matched to player position and season phase.',
        injuries: [
          { name: 'ACL rupture', description: '6–9 months phased rehab with Return to Sport testing.', diagnosisSlug: 'acl-rehabilitacija-beograd' },
          { name: 'Meniscus injury', description: 'Often with ACL. Conservative or post-op protocol.', diagnosisSlug: 'meniskus-rehabilitacija-beograd' },
          { name: 'Hamstring strain', description: 'Most common soft injury. Nordics + progressive sprint protocol.' },
          { name: 'Adductor syndrome (groin)', description: 'Chronic groin pain — Copenhagen Adduction protocol.' },
          { name: 'Ankle sprain', description: 'Acute protocol + neuromuscular training to prevent recurrence.' },
          { name: 'Achilles tendinopathy', description: 'High risk for goalkeepers and jumping-intensive roles.', diagnosisSlug: 'ahilova-tetiva-tretman-beograd' },
        ],
        protocol: [
          { title: '1. Assessment and clinical tests', description: 'Lachman, McMurray, Thessaly — plus functional tests (Y-Balance, hop tests).' },
          { title: '2. Acute phase and swelling control', description: 'HILT laser, manual, kinesiotaping.' },
          { title: '3. Mobility restoration', description: 'Manual + passive/active mobility.' },
          { title: '4. Strength and neuromuscular control', description: 'Progressive load, plyometrics, single-leg stability.' },
          { title: '5. Sport-specific drills', description: 'Sprints, cutting, kick mechanics — staged load.' },
          { title: '6. Return to Play tests', description: 'Hop tests, isokinetic assessment, clinical clearance.' },
        ],
        prevention: ['FIFA 11+ warm-up before every session', 'Nordics 2x/week', 'Copenhagen Adduction for groin', 'Single-leg balance', 'Adequate recovery — min 48h between heavy sessions'],
        faqs: [
          { question: 'How long until match play after ACL surgery?', answer: 'Standard is 9–12 months with passed Return to Sport tests.' },
          { question: 'Can I play with chronic groin pain?', answer: 'Short-term with load adjustment and therapy. Long-term it may progress to sports hernia.' },
          { question: 'Do prevention programs work?', answer: 'Yes. FIFA 11+ reduces serious-injury incidence by 30–50% per meta-analyses.' },
        ],
      },
      ru: {
        metaTitle: 'Физиотерапия для футболистов Белград | PHYSIOWRLD',
        metaDesc: 'Реабилитация футбольных травм в Белграде — ACL, мениск, хамстринг, пах. PHYSIOWRLD.',
        h1: 'Физиотерапия для футболистов',
        intro: 'Футбол сочетает спринты, контакт и резкие торможения — лидер по травмам ACL, мениска и хамстринга.',
        injuries: [
          { name: 'Разрыв ACL', description: '6–9 мес фазированной реабилитации.', diagnosisSlug: 'acl-rehabilitacija-beograd' },
          { name: 'Травма мениска', description: 'Часто с ACL.', diagnosisSlug: 'meniskus-rehabilitacija-beograd' },
          { name: 'Растяжение хамстринга', description: 'Нордики + спринт-протокол.' },
          { name: 'Аддукторный синдром', description: 'Copenhagen Adduction.' },
          { name: 'Растяжение голеностопа', description: 'Нейромышечный тренинг.' },
        ],
        protocol: [
          { title: '1. Оценка', description: 'Клинические и функциональные тесты.' },
          { title: '2. Острая фаза', description: 'HILT, мануал, тейпы.' },
          { title: '3. Подвижность', description: 'Мобилизация.' },
          { title: '4. Сила', description: 'Плиометрика, single-leg.' },
          { title: '5. Спорт-специфика', description: 'Спринты, cutting.' },
          { title: '6. Return to Play', description: 'Hop тесты, изокинетика.' },
        ],
        prevention: ['FIFA 11+', 'Нордики 2 р/нед', 'Copenhagen для паха', 'Single-leg баланс', 'Восстановление 48ч+'],
        faqs: [
          { question: 'Когда вернуться после ACL?', answer: '9–12 мес с пройденными Return to Sport тестами.' },
          { question: 'Можно ли играть с болью в паху?', answer: 'Кратко — да, долго — риск спортивной грыжи.' },
          { question: 'Работают ли программы профилактики?', answer: 'Да, FIFA 11+ снижает травмы на 30–50%.' },
        ],
      },
    },
  },
  {
    slug: 'fizioterapija-za-teretanu',
    name: { sr: 'Fizioterapija za teretanu', en: 'Physical therapy for gym training', ru: 'Физиотерапия для тренажёрного зала' },
    relatedServices: [
      { path: '/fizikalna-terapija-novi-beograd', label: { sr: 'Fizikalna terapija', en: 'Physical therapy', ru: 'Физиотерапия' } },
      { path: '/kiropraktika-novi-beograd', label: { sr: 'Kiropraktika', en: 'Chiropractic', ru: 'Хиропрактика' } },
      { path: '/services/functional-training', label: { sr: 'Funkcionalni trening', en: 'Functional training', ru: 'Функциональный тренинг' } },
    ],
    relatedDiagnoses: [
      'bol-u-ledjima-tretman-beograd',
      'bol-u-ramenu-tretman-beograd',
      'teniski-lakat-tretman-beograd',
      'bol-u-kolenu-tretman-beograd',
    ],
    i18n: {
      sr: {
        metaTitle: 'Fizioterapija za teretanu i bodybuilding | PHYSIOWRLD',
        metaDesc: 'Tretman povreda iz teretane u Beogradu — bol u leđima od čučnja, rame od bench-a, lakat od pull-up-a. PHYSIOWRLD klinika.',
        h1: 'Fizioterapija za polaznike teretane — povrede i tehnika',
        intro: 'Trening sa opterećenjem je bezbedan kada je tehnika dobra, ali pogrešan obrazac pokreta brzo proizvodi karakteristične povrede ramena, leđa i lakata. PHYSIOWRLD spaja kliničku procenu sa korekcijom tehnike u teretani.',
        injuries: [
          { name: 'Bol u donjim leđima od čučnja/deadlift-a', description: 'Najčešće butt-wink ili gubitak neutralne kičme pod opterećenjem. Korekcija + jačanje kor.', diagnosisSlug: 'bol-u-ledjima-tretman-beograd' },
          { name: 'Subakromialni impingement (bench/overhead)', description: 'Bol u prednjem i bočnom delu ramena. Korekcija mehanike + scapular control.', diagnosisSlug: 'bol-u-ramenu-tretman-beograd' },
          { name: 'Lateralni epikondilitis (pull-ups, curls)', description: 'Bol sa spoljne strane lakta — Shockwave + ekscentrike.', diagnosisSlug: 'teniski-lakat-tretman-beograd' },
          { name: 'Patelarna tendinopatija (jumper\'s knee)', description: 'Bol ispod kolena pri čučnju. Progresivni protokol + Shockwave.', diagnosisSlug: 'bol-u-kolenu-tretman-beograd' },
          { name: 'SLAP / labralna povreda ramena', description: 'Težak bench press i bolni pokreti iznad glave. Procena + konzervativni protokol.' },
        ],
        protocol: [
          { title: '1. Klinička procena + analiza tehnike', description: 'Video analiza čučnja, deadlift-a, bench-a — identifikacija obrazaca.' },
          { title: '2. Tretman akutnih simptoma', description: 'HILT laser, manuelne tehnike, kiropraktika kad je indikovano.' },
          { title: '3. Korekcija obrasca pokreta', description: 'Mobilizacija deficitarnih zglobova + neuromuskularno reedukovanje.' },
          { title: '4. Progresivni return to lift', description: 'Postupno vraćanje opterećenja sa korekcijom tehnike.' },
          { title: '5. Prevencija budućih povreda', description: 'Plan održavanja, mobilnost i accessory rad.' },
        ],
        prevention: [
          'Zagrejte se 10 minuta pre prvog radnog seta.',
          'Ne menjajte intenzitet više od 5–10% nedeljno.',
          'Mobilnost ramena i kuka — minimum 15 min, 3x nedeljno.',
          'Snimite tehniku — vizuelni feedback rešava većinu grešaka.',
          'Ne ignorišite bol koji traje duže od 3 treninga — to nije DOMS.',
        ],
        faqs: [
          { question: 'Mogu li trenirati sa bolom u donjim leđima?', answer: 'Zavisi od izvora bola. Ako nema neuroloških znakova i bol je mehanički, modifikacija (smanjenje obima, alternativne vežbe) je adekvatna. Pre nastavka — procena.' },
          { question: 'Da li je kiropraktika korisna za polaznike teretane?', answer: 'Da, kad postoji segmentno ograničenje koje sprečava korektnu mehaniku. Kiropraktika + jačanje daju najbolji ishod.' },
          { question: 'Koliko pauze posle povrede ramena?', answer: 'Tipično 2–6 nedelja modifikovanog treninga — totalna pauza je retko potrebna i može pogoršati ishod.' },
        ],
      },
      en: {
        metaTitle: 'Physical therapy for gym training | PHYSIOWRLD',
        metaDesc: 'Gym injury treatment in Belgrade — low back from squats, shoulder from bench, elbow from pull-ups. PHYSIOWRLD clinic.',
        h1: 'Physical therapy for gym goers — injuries and technique',
        intro: 'Resistance training is safe with good technique, but faulty movement patterns produce characteristic shoulder, back and elbow injuries. PHYSIOWRLD combines clinical assessment with technique correction in the gym.',
        injuries: [
          { name: 'Low back pain from squat/deadlift', description: 'Usually butt-wink or loss of neutral spine under load. Correction + core strengthening.', diagnosisSlug: 'bol-u-ledjima-tretman-beograd' },
          { name: 'Subacromial impingement (bench/overhead)', description: 'Anterior/lateral shoulder pain. Mechanics correction + scapular control.', diagnosisSlug: 'bol-u-ramenu-tretman-beograd' },
          { name: 'Lateral epicondylitis (pull-ups, curls)', description: 'Lateral elbow pain — Shockwave + eccentrics.', diagnosisSlug: 'teniski-lakat-tretman-beograd' },
          { name: 'Patellar tendinopathy (jumper\'s knee)', description: 'Below-kneecap pain. Progressive protocol + Shockwave.', diagnosisSlug: 'bol-u-kolenu-tretman-beograd' },
          { name: 'SLAP / labral shoulder injury', description: 'Heavy bench and painful overhead motion. Assessment + conservative protocol.' },
        ],
        protocol: [
          { title: '1. Clinical assessment + technique analysis', description: 'Video analysis of squat, deadlift, bench.' },
          { title: '2. Acute symptom treatment', description: 'HILT laser, manual, chiropractic when indicated.' },
          { title: '3. Movement pattern correction', description: 'Mobilization + neuromuscular re-education.' },
          { title: '4. Progressive return to lift', description: 'Gradual load return with technique correction.' },
          { title: '5. Prevention plan', description: 'Maintenance, mobility, accessory work.' },
        ],
        prevention: ['10-min warm-up before first working set', 'Don\'t change intensity >5–10% per week', 'Shoulder + hip mobility 15 min, 3x/week', 'Film your technique', 'Pain lasting >3 sessions is not DOMS'],
        faqs: [
          { question: 'Can I train with low back pain?', answer: 'Depends on the source. Without neurological signs and with mechanical pain — modification is fine. Assessment first.' },
          { question: 'Is chiropractic useful for gym goers?', answer: 'Yes, when segmental restriction blocks correct mechanics. Chiro + strength gives the best outcome.' },
          { question: 'How long off after a shoulder injury?', answer: 'Typically 2–6 weeks of modified training — total rest is rarely needed.' },
        ],
      },
      ru: {
        metaTitle: 'Физиотерапия для тренажёрного зала | PHYSIOWRLD',
        metaDesc: 'Лечение травм из зала в Белграде — поясница от приседа, плечо от жима, локоть от подтягиваний. PHYSIOWRLD.',
        h1: 'Физиотерапия для посетителей зала',
        intro: 'Силовой тренинг безопасен при хорошей технике, но ошибки быстро дают типичные травмы.',
        injuries: [
          { name: 'Поясница от приседа/тяги', description: 'Butt-wink, потеря нейтрали.', diagnosisSlug: 'bol-u-ledjima-tretman-beograd' },
          { name: 'Импинджмент плеча', description: 'Коррекция механики + скапулярный контроль.', diagnosisSlug: 'bol-u-ramenu-tretman-beograd' },
          { name: 'Локоть теннисиста', description: 'УВТ + эксцентрика.', diagnosisSlug: 'teniski-lakat-tretman-beograd' },
          { name: 'Колено прыгуна', description: 'Прогрессивный протокол.', diagnosisSlug: 'bol-u-kolenu-tretman-beograd' },
        ],
        protocol: [
          { title: '1. Оценка и техника', description: 'Видеоанализ.' },
          { title: '2. Острые симптомы', description: 'HILT, мануал.' },
          { title: '3. Коррекция паттерна', description: 'Мобилизация + реэдукация.' },
          { title: '4. Return to lift', description: 'Постепенный возврат.' },
          { title: '5. Профилактика', description: 'Мобильность.' },
        ],
        prevention: ['Разминка 10 мин', 'Прирост 5–10%/нед', 'Мобильность 15 мин 3 р/нед', 'Видеоконтроль', 'Боль >3 тренировок — оценка'],
        faqs: [
          { question: 'Тренировки с болью в пояснице?', answer: 'Без неврологии — с модификацией. Сначала оценка.' },
          { question: 'Хиропрактика для зала?', answer: 'Да, при сегментных ограничениях.' },
          { question: 'Пауза после плеча?', answer: '2–6 недель модифицированного тренинга.' },
        ],
      },
    },
  },
  {
    slug: 'fizioterapija-za-skijase',
    name: { sr: 'Fizioterapija za skijaše', en: 'Physical therapy for skiers', ru: 'Физиотерапия для лыжников' },
    relatedServices: [
      { path: '/fizikalna-terapija-novi-beograd', label: { sr: 'Fizikalna terapija', en: 'Physical therapy', ru: 'Физиотерапия' } },
      { path: '/services/functional-training', label: { sr: 'Funkcionalni trening', en: 'Functional training', ru: 'Функциональный тренинг' } },
      { path: '/kiropraktika-novi-beograd', label: { sr: 'Kiropraktika', en: 'Chiropractic', ru: 'Хиропрактика' } },
    ],
    relatedDiagnoses: [
      'acl-rehabilitacija-beograd',
      'meniskus-rehabilitacija-beograd',
      'bol-u-kolenu-tretman-beograd',
      'bol-u-ledjima-tretman-beograd',
    ],
    i18n: {
      sr: {
        metaTitle: 'Fizioterapija za skijaše Beograd | PHYSIOWRLD',
        metaDesc: 'Pre-sezonska priprema skijaša u Beogradu — kolena, leđa, ravnoteža. Rehabilitacija ACL i meniskus povreda. PHYSIOWRLD klinika.',
        h1: 'Fizioterapija za skijaše — pre-sezonska priprema i rehabilitacija',
        intro: 'Skijanje je sezonski sport sa visokim opterećenjem kolena i leđa. Većina povreda dešava se kod nepripremljenih skijaša u prvim danima sezone. Pre-sezonska priprema značajno smanjuje rizik.',
        injuries: [
          { name: 'Ruptura ACL-a', description: 'Klasična skijaška povreda — često mehanizam „phantom foot". 6–9 meseci faza-po-faza rehabilitacije.', diagnosisSlug: 'acl-rehabilitacija-beograd' },
          { name: 'Povreda meniskusa', description: 'Često uz ACL ili izolovano kod rotacije.', diagnosisSlug: 'meniskus-rehabilitacija-beograd' },
          { name: 'MCL distenzija', description: 'Valgus opterećenje kolena — uglavnom konzervativno.' },
          { name: 'Bol u donjim leđima', description: 'Statična pozicija u stezanju — kor stabilnost i mobilnost grudne kičme.', diagnosisSlug: 'bol-u-ledjima-tretman-beograd' },
          { name: 'Patelofemoralni sindrom', description: 'Bol oko čašice posle dugih dana na stazi.', diagnosisSlug: 'bol-u-kolenu-tretman-beograd' },
        ],
        protocol: [
          { title: '1. Pre-sezonska procena (8–10 nedelja pre sezone)', description: 'Funkcionalna procena — Y-Balance, single-leg squat, hop testovi.' },
          { title: '2. Razvoj snage kvadricepsa i gluteusa', description: 'Progresivni snazi trening 2–3x nedeljno.' },
          { title: '3. Plyometrika i ekscentrike', description: 'Skok-spuštanje, ekscentrične squat varijante.' },
          { title: '4. Ravnoteža i propriocepcija', description: 'Single-leg, BOSU, neuromuskularni drilovi.' },
          { title: '5. Pre-sezonski tapering', description: 'Smanjenje obima 1 nedelju pre prvog skijanja.' },
        ],
        prevention: [
          'Počnite pripremu minimum 6 nedelja pre prvog skijanja.',
          'Fokus na ekscentrične vežbe kvadricepsa.',
          'Snažan gluteus medius — ključ za stabilnost kolena.',
          'Trening ravnoteže 10 min, 3x nedeljno.',
          'Pravilno odabrana i podešena oprema (binding release).',
        ],
        faqs: [
          { question: 'Koliko je pre-sezonska priprema duga?', answer: 'Minimalno 6 nedelja, idealno 8–10, sa 2–3 treninga nedeljno fokusiranih na snagu, ekscentrike i ravnotežu.' },
          { question: 'Kada je bezbedan povratak na skije posle ACL operacije?', answer: 'Tipično 12 meseci uz uspešno položene Return to Sport testove. Pre povratka — sport-specific simulacija opterećenja.' },
          { question: 'Šta ako počnem osetiti bol u kolenu prvog dana?', answer: 'Ne nastavljajte sa simptomima. Klinička procena pre nastavka sezone — najčešće patelofemoralni sindrom koji se brzo rešava korekcijom opterećenja.' },
        ],
      },
      en: {
        metaTitle: 'Physical therapy for skiers Belgrade | PHYSIOWRLD',
        metaDesc: 'Pre-season skier prep in Belgrade — knees, back, balance. ACL & meniscus rehab. PHYSIOWRLD clinic.',
        h1: 'Physical therapy for skiers — pre-season prep and rehab',
        intro: 'Skiing is a seasonal high-load sport for knees and back. Most injuries hit unprepared skiers in the first days of the season. Pre-season prep substantially reduces risk.',
        injuries: [
          { name: 'ACL rupture', description: 'Classic ski injury — often "phantom foot" mechanism. 6–9 months phased rehab.', diagnosisSlug: 'acl-rehabilitacija-beograd' },
          { name: 'Meniscus injury', description: 'Often with ACL or isolated on rotation.', diagnosisSlug: 'meniskus-rehabilitacija-beograd' },
          { name: 'MCL sprain', description: 'Valgus knee load — usually conservative.' },
          { name: 'Low back pain', description: 'Static braced position — core stability and thoracic mobility.', diagnosisSlug: 'bol-u-ledjima-tretman-beograd' },
          { name: 'Patellofemoral syndrome', description: 'Knee pain after long days on slope.', diagnosisSlug: 'bol-u-kolenu-tretman-beograd' },
        ],
        protocol: [
          { title: '1. Pre-season assessment (8–10 weeks out)', description: 'Y-Balance, single-leg squat, hop tests.' },
          { title: '2. Quad and glute strength', description: 'Progressive 2–3x/week.' },
          { title: '3. Plyometrics and eccentrics', description: 'Drop jumps, eccentric squats.' },
          { title: '4. Balance and proprioception', description: 'Single-leg, BOSU, neuromuscular drills.' },
          { title: '5. Pre-season taper', description: 'Volume reduction 1 week out.' },
        ],
        prevention: ['Start at least 6 weeks pre-season', 'Focus on quad eccentrics', 'Strong glute med = knee stability', 'Balance training 10 min, 3x/week', 'Properly set bindings'],
        faqs: [
          { question: 'How long is pre-season prep?', answer: 'Minimum 6, ideally 8–10 weeks, 2–3 sessions/week — strength, eccentrics, balance.' },
          { question: 'When can I ski after ACL surgery?', answer: 'Typically 12 months with passed Return to Sport tests and sport-specific load simulation.' },
          { question: 'Knee pain on day one?', answer: 'Don\'t push through. Assessment before continuing — usually PFPS that resolves with load correction.' },
        ],
      },
      ru: {
        metaTitle: 'Физиотерапия для лыжников Белград | PHYSIOWRLD',
        metaDesc: 'Предсезонная подготовка лыжников в Белграде — колени, спина, баланс. PHYSIOWRLD.',
        h1: 'Физиотерапия для лыжников',
        intro: 'Горные лыжи — сезонный спорт с высокой нагрузкой на колени и спину. Большинство травм у неподготовленных в первые дни сезона.',
        injuries: [
          { name: 'Разрыв ACL', description: 'Классическая травма — «phantom foot».', diagnosisSlug: 'acl-rehabilitacija-beograd' },
          { name: 'Травма мениска', description: 'Часто с ACL.', diagnosisSlug: 'meniskus-rehabilitacija-beograd' },
          { name: 'Растяжение MCL', description: 'Вальгус — обычно консервативно.' },
          { name: 'Боль в пояснице', description: 'Стабильность кора + мобильность груди.', diagnosisSlug: 'bol-u-ledjima-tretman-beograd' },
          { name: 'PFPS', description: 'Боль вокруг чашки.', diagnosisSlug: 'bol-u-kolenu-tretman-beograd' },
        ],
        protocol: [
          { title: '1. Оценка за 8–10 нед', description: 'Y-Balance, hop тесты.' },
          { title: '2. Сила квадра/глют.', description: '2–3 раза в нед.' },
          { title: '3. Плиометрика', description: 'Дроп-джампы, эксцентрика.' },
          { title: '4. Баланс', description: 'Single-leg, BOSU.' },
          { title: '5. Тейпер', description: 'Снижение объёма за неделю.' },
        ],
        prevention: ['От 6 недель', 'Эксцентрика квадра', 'Сильный glute med', 'Баланс 10 мин 3 р/нед', 'Настроенные крепления'],
        faqs: [
          { question: 'Сколько готовиться?', answer: 'От 6, идеально 8–10 недель.' },
          { question: 'Когда на лыжи после ACL?', answer: 'Около 12 месяцев с тестами.' },
          { question: 'Боль в колене с первого дня?', answer: 'Не игнорировать — оценка.' },
        ],
      },
    },
  },
];

export const SPORT_MAP: Record<string, Sport> = Object.fromEntries(
  SPORTS.map((s) => [s.slug, s])
);
